// danmaku-component.js - 修复 shadowRoot 错误，兼容生产环境
class DanmakuLayer extends HTMLElement {
  static get observedAttributes() {
    return ['width', 'height', 'background', 'fontsize', 'speed', 'color', 'autostart', 'hover-pause', 'click-event', 'ws-url', 'user-id', 'site'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' }); // shadowRoot 自动可用

    this.danmakus = [];
    this.animationId = null;
    this.isRunning = false;
    this.canvas = null;
    this.ctx = null;
    this.mouseX = -1;
    this.mouseY = -1;
    this.maxDanmakuCount = 100;
    this.displayDuration = 10000;
    this.timeoutMap = new WeakMap();
    this.ws = null;
    this.wsUrl = 'ws://localhost:8000/ws';
    this.reconnectTimer = null;
    this.userId = 'anonymous';
    this.site = '';

    this.config = {
      width: '100%',
      height: '300px',
      background: '#FFFEFE',
      fontSize: 16,
      speed: 1,
      color: '#EE6E96',
      autostart: true,
      hoverPause: true,
      clickEvent: true,
    };

    this._handleMouseMove = this._handleMouseMove.bind(this);
    this._handleMouseLeave = this._handleMouseLeave.bind(this);
    this._handleClick = this._handleClick.bind(this);
    this._handleResize = this._handleResize.bind(this);

    this.presetMessages = [
      { text: 'Amazing quality!', color: '#000' },
      { text: 'Highly recommend!', color: '#000' },
      { text: 'Super fast shipping', color: '#000' },
      { text: 'Just placed my order', color: '#000' },
      { text: 'Absolutely love it!', color: '#000' },
      { text: 'Perfect gift idea', color: '#000' },
      { text: 'Exceeded expectations', color: '#000' },
      { text: 'Worth every penny', color: '#000' },
      { text: 'Game changer!', color: '#000' },
      { text: 'Premium feel', color: '#000' },
      { text: 'Arrived in perfect condition', color: '#000' },
      { text: 'Smooth experience', color: '#000' },
      { text: 'Exactly what I needed', color: '#000' },
      { text: 'Can\'t stop using it', color: '#000' },
      { text: 'Great design', color: '#000' },
      { text: 'Customer service is top-notch', color: '#000' },
      { text: 'Lightning fast delivery', color: '#000' },
      { text: 'Best purchase this year', color: '#000' },
      { text: 'Looks even better in person', color: '#000' },
      { text: 'Will buy again', color: '#000' },
      { text: 'Looks even better in person', color: '#000' },
      { text: 'The packaging is so elegant, Mom will love it!', color: '#000' },
      { text: 'Great quality! Worth every penny.', color: '#000' },
      { text: 'So glad I found this for her!', color: '#000' },
      { text: 'My mom was so moved by this gift.', color: '#000' },
      
      // --- 物流与时效 (Shipping & Urgency) ---
      { text: 'Super fast shipping, just in time for Mother’s Day!', color: '#000' },
      { text: 'Order now to get it before May 10th!', color: '#000' },
      { text: 'Expedited shipping was a lifesaver!', color: '#000' },
      { text: 'Tracked my order, it’s arriving tomorrow!', color: '#000' },
      
      // --- 下单动态 (Live Purchase Style) ---
      { text: 'Someone from London just purchased a Gift Set', color: '#000' },
      { text: 'Sarah from Paris just bought the Pearl Necklace', color: '#000' },
      { text: 'A customer in Berlin just ordered the 2-piece Bundle', color: '#000' },
      { text: 'Just sold our 500th Mother’s Day Gift!', color: '#000' },
      
      // --- 温馨寄语 (Emotional & Seasonal) ---
      { text: 'Make her feel like a queen this year.', color: '#000' },
      { text: 'The perfect way to say "Thank you, Mom"', color: '#000' },
      { text: 'Gifts as unique as she is.', color: '#000' },
      { text: 'Celebrating all the amazing Moms worldwide!', color: '#000' },
      { text: 'Sent this directly to my mom, she’s so excited!', color: '#000' }
    ];
    this.presetTimer = null;
  }

  connectedCallback() {
    this._parseAttributes();
    this._render();
    this._bindEvents();
    if (this.config.autostart) this.start();
    this._connectWebSocket();
  }

  disconnectedCallback() {
    this.stop();
    this._unbindEvents();
    this.clear();
    if (this.ws) this.ws.close();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (oldVal === newVal) return;
    this._parseAttributes();
    if (this.canvas) {
      this._updateCanvasSize();
      this._updateContainerStyle();
    }
    if (name === 'ws-url' && this.isConnected) {
      if (this.ws) this.ws.close();
      this._connectWebSocket();
    }
  }

  _parseAttributes() {
    const getAttr = (name, def) => {
      const v = this.getAttribute(name);
      return v !== null ? v : def;
    };
    this.config.width = getAttr('width', '100%');
    this.config.height = getAttr('height', '300px');
    this.config.background = getAttr('background', '#FFFEFE');
    this.config.fontSize = parseInt(getAttr('fontsize', '16'), 10);
    this.config.speed = parseFloat(getAttr('speed', '1'));
    this.config.color = getAttr('color', '#EE6E96');
    this.config.autostart = getAttr('autostart') !== 'false';
    this.config.hoverPause = getAttr('hover-pause') !== 'false';
    this.config.clickEvent = getAttr('click-event') !== 'false';
    this.wsUrl = getAttr('ws-url', 'ws://localhost:8000/ws');
    this.userId = getAttr('user-id', 'anonymous');
    this.site = getAttr('site', window.location.hostname);
  }

  _render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; }
        .container {
          position: relative;
          width: ${this.config.width};
          height: ${this.config.height};
          background: ${this.config.background};
          overflow: hidden;
        }
        canvas {
          position: absolute;
          top: 0; left: 0;
          width: 100%; height: 100%;
          display: block;
          pointer-events: auto;
        }
        .content-slot {
          position: relative;
          width: 100%; height: 100%;
          pointer-events: none;
        }
        canvas.hoverable { cursor: default; }
        canvas.hoverable:hover { cursor: pointer; }
      </style>
      <div class="container">
        <div class="content-slot"><slot></slot></div>
        <canvas></canvas>
      </div>
    `;
    this.canvas = this.shadowRoot.querySelector('canvas');
    this.ctx = this.canvas.getContext('2d');
    if (this.config.clickEvent) this.canvas.classList.add('hoverable');
    this._updateCanvasSize();
  }

  _updateCanvasSize() {
    if (!this.canvas) return;
    const container = this.shadowRoot.querySelector('.container');
    const rect = container.getBoundingClientRect();
    this.canvas.width = rect.width;
    this.canvas.height = rect.height;
  }

  _updateContainerStyle() {
    const container = this.shadowRoot.querySelector('.container');
    if (container) {
      container.style.width = this.config.width;
      container.style.height = this.config.height;
      container.style.background = this.config.background;
    }
    this._updateCanvasSize();
  }

  _bindEvents() {
    if (!this.canvas) return;
    this.canvas.addEventListener('mousemove', this._handleMouseMove);
    this.canvas.addEventListener('mouseleave', this._handleMouseLeave);
    if (this.config.clickEvent) this.canvas.addEventListener('click', this._handleClick);
  }

  _unbindEvents() {
    if (!this.canvas) return;
    this.canvas.removeEventListener('mousemove', this._handleMouseMove);
    this.canvas.removeEventListener('mouseleave', this._handleMouseLeave);
    this.canvas.removeEventListener('click', this._handleClick);
  }

  _handleResize() { this._updateCanvasSize(); }

  _handleMouseMove(e) {
    if (!this.config.hoverPause && !this.config.clickEvent) return;
    const rect = this.canvas.getBoundingClientRect();
    const scaleX = this.canvas.width / rect.width;
    const scaleY = this.canvas.height / rect.height;
    this.mouseX = (e.clientX - rect.left) * scaleX;
    this.mouseY = (e.clientY - rect.top) * scaleY;
    if (this.config.hoverPause) this._updateHoverPauseState();
  }

  _handleMouseLeave() {
    this.mouseX = this.mouseY = -1;
    if (this.config.hoverPause) this.danmakus.forEach(dm => dm.paused = false);
  }

  _handleClick(e) {
    if (!this.config.clickEvent) return;
    const rect = this.canvas.getBoundingClientRect();
    const scaleX = this.canvas.width / rect.width;
    const scaleY = this.canvas.height / rect.height;
    const clickX = (e.clientX - rect.left) * scaleX;
    const clickY = (e.clientY - rect.top) * scaleY;
    for (let i = this.danmakus.length - 1; i >= 0; i--) {
      const dm = this.danmakus[i];
      if (this._isPointInDanmaku(clickX, clickY, dm)) {
        this.dispatchEvent(new CustomEvent('danmaku-click', {
          detail: { text: dm.text, color: dm.color, fontSize: dm.fontSize, danmaku: dm },
          bubbles: true, composed: true
        }));
        break;
      }
    }
  }

  _isPointInDanmaku(px, py, dm) {
    if (!this.ctx) return false;
    this.ctx.font = `${dm.fontSize}px "Microsoft YaHei", sans-serif`;
    const w = this.ctx.measureText(dm.text).width;
    const h = dm.fontSize;
    return px >= dm.x && px <= dm.x + w && py >= dm.y - h && py <= dm.y;
  }

  _updateHoverPauseState() {
    if (this.mouseX < 0 || this.mouseY < 0) return;
    this.danmakus.forEach(dm => dm.paused = this._isPointInDanmaku(this.mouseX, this.mouseY, dm));
  }

  start() {
    if (this.isRunning) return;
    this.isRunning = true;
    window.addEventListener('resize', this._handleResize);
    this._animate();
  }

  stop() {
    if (this.animationId) cancelAnimationFrame(this.animationId);
    this.isRunning = false;
    window.removeEventListener('resize', this._handleResize);

    if (this.presetTimer) {
      clearInterval(this.presetTimer);
      this.presetTimer = null;
    }
  }

  _animate() {
    if (!this.isRunning || !this.ctx || !this.canvas) return;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    const isMobile = window.innerWidth < 768;
    const rowCount = isMobile ? 6 : 3; 
    const padding = isMobile ? 10 : 20; // 这里的 padding 是上下留白
    const availableHeight = this.canvas.height - (padding * 2);
    const rowHeight = availableHeight / rowCount;

    // 绘制分割线
    if (!isMobile) {
      this.ctx.beginPath();
      this.ctx.strokeStyle = 'rgba(238, 110, 150, 0.2)'; 
      this.ctx.lineWidth = 1;
      for (let i = 1; i <= rowCount; i++) {
        const lineY = Math.floor(padding + i * rowHeight) + 0.5;
        this.ctx.moveTo(0, lineY);
        this.ctx.lineTo(this.canvas.width, lineY);
      }
      this.ctx.stroke();
    }
  // --- 【分割线绘制结束】 ---

    for (let i = this.danmakus.length - 1; i >= 0; i--) {
      const dm = this.danmakus[i];
      this.ctx.font = `${dm.fontSize}px "MiSans Latin VF", sans-serif`;
      this.ctx.textBaseline = 'middle';
      this.ctx.fillStyle = dm.color;
      this.ctx.fillText(dm.text, dm.x, dm.y);
      this.ctx.shadowBlur = 0;
      if (!dm.paused) dm.x -= dm.speed;
      const w = this.ctx.measureText(dm.text).width;
      if (dm.x + w < 0) this._removeDanmaku(dm);
    }
    this.animationId = requestAnimationFrame(() => this._animate());
  }

  _addDanmakuInternal(text, color, fontSize, speed) {
    if (!this.canvas) return;
    const x = this.canvas.width;

    // --- 修改开始：三行轨道逻辑 ---
    const isMobile = window.innerWidth < 768;
    const rowCount = isMobile ? 6 : 3; 
    const padding = isMobile ? 10 : 20;
    const availableHeight = this.canvas.height - (padding * 2);
    const rowHeight = availableHeight / rowCount;
    
    // 如果没有初始化轨道计数器，则初始化一个（用于轮流排队）
    const randomTrack = Math.floor(Math.random() * rowCount);
    const y = padding + randomTrack * rowHeight + (rowHeight / 2);
    const finalFontSize = isMobile ? 14 : fontSize;
    // 计数器递增，确保下一条弹幕去下一行
    this._trackIndex++;
    // --- 修改结束 ---

    const dm = { 
      text, 
      x, 
      y, 
      speed: speed * (0.8 + Math.random() * 0.6), 
      color, 
      fontSize: finalFontSize, 
      paused: false 
    };
    
    this.danmakus.push(dm);
    if (this.danmakus.length > this.maxDanmakuCount) this._removeDanmaku(this.danmakus[0]);
    const timer = setTimeout(() => this._removeDanmaku(dm), this.displayDuration);
    this.timeoutMap.set(dm, timer);
    // const x = this.canvas.width;
    // const y = Math.random() * (this.canvas.height - fontSize) + fontSize;
    // const dm = { text, x, y, speed: speed * (0.8 + Math.random() * 0.6), color, fontSize, paused: false };
    // this.danmakus.push(dm);
    // if (this.danmakus.length > this.maxDanmakuCount) this._removeDanmaku(this.danmakus[0]);
    // const timer = setTimeout(() => this._removeDanmaku(dm), this.displayDuration);
    // this.timeoutMap.set(dm, timer);
  }

  _removeDanmaku(dm) {
    const idx = this.danmakus.indexOf(dm);
    if (idx > -1) {
      const timer = this.timeoutMap.get(dm);
      if (timer) clearTimeout(timer);
      this.timeoutMap.delete(dm);
      this.danmakus.splice(idx, 1);
    }
  }

  clear() {
    this.danmakus.forEach(dm => {
      const timer = this.timeoutMap.get(dm);
      if (timer) clearTimeout(timer);
    });
    this.timeoutMap = new WeakMap();
    this.danmakus = [];
  }

  addDanmaku(text, color = this.config.color, fontSize = this.config.fontSize, speed = this.config.speed) {
    const trimmed = String(text).trim();
    if (!trimmed || trimmed.length > 50) {
      this.dispatchEvent(new CustomEvent('danmaku-error', { detail: { error: 'The content is empty or too long' }, bubbles: true, composed: true }));
      return false;
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ text: trimmed, color, fontSize, speed, userId: this.userId, site: this.site }));
      localStorage.setItem('danmu_send', 'yes');
      this.dispatchEvent(new CustomEvent('danmaku-sent', { bubbles: true, composed: true }));
      return true;
    } else {
      this.dispatchEvent(new CustomEvent('danmaku-error', { detail: { error: 'Server Error' }, bubbles: true, composed: true }));
      return false;
    }
  }

  _connectWebSocket() {
    if (this.ws) this.ws.close();
    try {
      this.ws = new WebSocket(this.wsUrl);
    } catch (e) {
      this._scheduleReconnect();
      return;
    }
    this.ws.onopen = async () => {
        this.dispatchEvent(new CustomEvent('danmaku-connected', { bubbles: true, composed: true }));
        // 拉取历史弹幕
        await this._fetchHistory();
    };
    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.text) this._addDanmakuInternal(data.text, data.color, data.fontSize, data.speed);
        else if (data.error) this.dispatchEvent(new CustomEvent('danmaku-error', { detail: { error: data.error }, bubbles: true, composed: true }));
      } catch (e) {}
    };
    this.ws.onerror = () => {};
    this.ws.onclose = () => this._scheduleReconnect();

    this.ws.onopen = async () => {
      this.dispatchEvent(new CustomEvent('danmaku-connected', { bubbles: true, composed: true }));
      await this._fetchHistory();
      this._startPresetLoop();  // 新增：启动预设弹幕
    };
  }

  _startPresetLoop() {
  if (this.presetTimer) clearInterval(this.presetTimer);
  if (!this.presetMessages || this.presetMessages.length === 0) return;

  this.presetTimer = setInterval(() => {
    // 随机取一条预设消息
    const item = this.presetMessages[Math.floor(Math.random() * this.presetMessages.length)];
    // 直接本地渲染，不发送 WebSocket
    this._addDanmakuInternal(
      item.text,
      item.color,
      this.config.fontSize,
      this.config.speed
    );
  }, this.autoInterval || 5000);
}
  _scheduleReconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      if (this.isConnected) this._connectWebSocket();
    }, 5000);
  }

  // 新增方法：拉取历史
  // danmaku-component.js 片段

  async _fetchHistory() {
    let base = this.wsUrl;
    // 移除路径部分 /ws，保留协议和域名
    base = base.replace(/\/ws$/, '');
    // 替换协议
    if (base.startsWith('wss://')) {
        base = base.replace('wss://', 'https://');
    } else if (base.startsWith('ws://')) {
        base = base.replace('ws://', 'http://');
    }
    try {
        const url = `${base}/api/danmaku/history?site=${encodeURIComponent(this.site)}&limit=50`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data.history && Array.isArray(data.history)) {
            const canvasWidth = this.canvas?.width || 1000;
            data.history.forEach((dm, i) => {
                  this._addDanmakuInternal(
                  dm.text,
                  dm.color || this.config.color,
                  dm.fontSize || this.config.fontSize,
                  dm.speed || this.config.speed
              );
              
              // 手动调整一下刚生成的这条弹幕的 X 坐标，让它们散开
              const lastDm = this.danmakus[this.danmakus.length - 1];
              if (lastDm) {
                  // 让历史弹幕根据索引 i 均匀分布在屏幕不同位置
                  lastDm.x = this.canvas.width - (i * 200); 
              }
            });
        }
    } catch (e) {
        console.warn('历史弹幕加载失败', e);
    }
}
  async drawLottery() {
    if (this.userId === 'anonymous') {
      this.dispatchEvent(new CustomEvent('lottery-error', { detail: { error: 'Login First' }, bubbles: true, composed: true }));
      return null;
    }
    // const base = this.wsUrl.replace('/ws', '');

    let base = this.wsUrl;
    // 移除路径部分 /ws，保留协议和域名
    base = base.replace(/\/ws$/, '');
    // 替换协议
    if (base.startsWith('wss://')) {
        base = base.replace('wss://', 'https://');
    } else if (base.startsWith('ws://')) {
        base = base.replace('ws://', 'http://');
    }

    try {
      const res = await fetch(`${base}/api/lottery/draw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: this.userId, site: this.site })
      });
      const data = await res.json();
      this.dispatchEvent(new CustomEvent('lottery-result', { detail: data, bubbles: true, composed: true }));
      return data;
    } catch (e) {
      this.dispatchEvent(new CustomEvent('lottery-error', { detail: { error: 'Network Error' }, bubbles: true, composed: true }));
      return null;
    }
  }

  async addPrizeToCart(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity }] })
    });
    if (!res.ok) throw new Error('Add to cart failed');
    return res.json();
  }
}

customElements.define('danmaku-layer', DanmakuLayer);